// `useAuthGuard()` — central auth-aware behaviour for `apps/web`.
//
// Wave 4.1 / US-015 (session-expiry handling).
//
// Behaviour:
//  - Subscribes to `useMe()` (cached at `['me']`).
//  - On 401 (`auth.session_expired` from the API): clears the cache, fires a
//    localised toast, and redirects the user to `/auth/login` with a `?from=`
//    return-to query param so a successful re-login can restore context.
//  - On 403: surfaces the message as a warning toast but stays on the route
//    (the page is still rendered; the action just isn't allowed).
//  - On 200: returns the resolved `user` so callers can branch on it.
//
// We deliberately do NOT block render here — TanStack Router's `beforeLoad`
// in `__root.tsx` is the authoritative gate. The hook is a behavioural
// listener layered on top so deep-tree consumers can bail out gracefully
// when the session expires *while* a page is open.

import { useEffect, useRef } from 'react';

import { ApiError, useMe, type AuthUser } from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { toast } from '@barel/ui';
import { useNavigate, useRouterState } from '@tanstack/react-router';

export interface UseAuthGuardOptions {
  /** Skip the listener entirely (e.g. on `/auth/*` pages). */
  enabled?: boolean;
}

export interface UseAuthGuardReturn {
  user: AuthUser | undefined;
  isLoading: boolean;
  isAuthenticated: boolean;
}

/**
 * Authoritative status hook for the authenticated shell. Fires toasts and
 * redirects on session expiry; returns the resolved user when present.
 */
export function useAuthGuard(options: UseAuthGuardOptions = {}): UseAuthGuardReturn {
  const enabled = options.enabled ?? true;
  const t = useT('auth');
  const tCommon = useT('common');
  const navigate = useNavigate();
  const routerState = useRouterState();
  // Inherit `useMe`'s transient-retry default (5xx / network blips retry;
  // 401/403 settle at once). A one-off backend hiccup must NOT surface as
  // `me.error` and bounce the user to login — only a *settled* 401 does.
  const me = useMe({ enabled });

  // Track the previous error so we don't fire the toast every render — only
  // on transitions (e.g. 200 → 401, undefined → 401).
  const previousErrorCodeRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const error = me.error;
    if (!error) {
      previousErrorCodeRef.current = null;
      return;
    }
    const code = ApiError.is(error) ? error.code : 'unknown';
    if (previousErrorCodeRef.current === code) return;
    previousErrorCodeRef.current = code;

    if (ApiError.is(error) && error.httpStatus === 401) {
      toast.error({
        title: t('session.expiredTitle'),
        description: t('session.expiredBody'),
      });
      const from = routerState.location.pathname;
      void navigate({
        to: '/auth/login',
        search: from && !from.startsWith('/auth') ? { from } : undefined,
        replace: true,
      });
      return;
    }
    if (ApiError.is(error) && error.httpStatus === 403) {
      toast.warning({
        title: t('session.forbiddenTitle'),
        description: t('session.forbiddenBody'),
      });
      return;
    }
    // Other failures surface as a generic transport error so the user knows
    // *something* went wrong and can retry.
    toast.error({ title: tCommon('states.error') });
  }, [enabled, me.error, navigate, routerState.location.pathname, t, tCommon]);

  return {
    user: me.data?.user,
    isLoading: me.isLoading,
    isAuthenticated: !!me.data?.user,
  };
}
